import { captureException } from '@sentry/nextjs';
import { requestServer } from '@repo/ui/src/utils/requestServer';
import { ShopFragment } from '@graphql/fragments';
import { ProductModelProps } from '@typings/product';
import { serverRuntimeConfig } from '@utils/serverRuntimeConfig';

export const searchProductListItems = /* GraphQL */ `
  query SearchProductListItems(
    $filter: SearchableProductFilterInput
    $pageSize: Int
    $currency: String
  ) {
    searchProductListItems(filter: $filter, pageSize: $pageSize) {
      hits {
        _meta {
          name
          schema
          deliveryKey
          deliveryId
          url
        }
        id
        title
        description
        color
        productData {
          categories {
            categories
          }
          sku
          newArrivalStart
          newArrivalEnd
        }
        shopData(currency: $currency) {
          ...Shop
        }
        images {
          name
          endpoint
          defaultHost
          url
        }
        ratingSummary {
          average
        }
      }
    }
  }
  ${ShopFragment}
`;

export interface QueryResponse {
  searchProductListItems: {
    hits: ProductModelProps[];
  };
}

interface PropsWithIDs {
  ids?: Array<string>;
  skus?: never;
  locale: string;
  currency: string;
  deliveryKey: string;
  preview?: boolean;
}
interface PropsWithSKUs {
  ids?: never;
  skus?: Array<string>;
  locale: string;
  currency: string;
  deliveryKey: string;
  preview?: boolean;
}

const getProductListData = async ({
  ids,
  skus,
  locale,
  currency,
  deliveryKey,
  preview,
}: PropsWithIDs | PropsWithSKUs) => {
  if (!ids?.length && !skus?.length) return [];
  try {
    const data = await requestServer<QueryResponse>(
      {
        document: searchProductListItems,
        variables: {
          filter: ids ? { ids } : { sku: skus },
          pageSize: ids?.length ?? skus?.length,
          currency: currency,
        },
        requestHeaders: {
          'Accept-Language': locale || 'en-US',
        },
      },
      {
        tags: deliveryKey ? [`page-${deliveryKey}`] : undefined,
        revalidate: preview ? 0 : serverRuntimeConfig.revalidate.pageTime,
      },
    );

    return data.searchProductListItems.hits;
  } catch (error) {
    captureException(error);
    return [];
  }
};

export default getProductListData;
